import { GridOptions, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Classes, Dialog } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { Buttons } from "modules/common/components/form";
import { Search } from "modules/common/components/form/Search";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { MAX_INT } from "modules/common/constants";
import { routes } from "modules/common/routes";
import { DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage";
import {
  MainPageRequestItemType,
  MainPageRequestsStore,
  MainPageRequestsStoreType,
  RequestTypes,
} from "modules/main/models/main-page-requests";
import { OrderTabs } from "modules/orders-manage/details/OrderDetailsTabsPage";
import React from "react";
import styles from "./RequestList.module.scss";
import { columns } from "./columns";

const TABLE_NAME = MainPageRequestsStore.name;
const sizeSaver = saveAgGridColumnSize(TABLE_NAME);

class RequestItems extends React.Component<RequestListProps, RequestListState> {
  constructor(props: RequestListProps) {
    super(props);

    this.state = {
      showRemoveOrder: false,
      showRemoveAgentRequest: false,
      request: null,
    };
  }

  private options: GridOptions = DefaultAgGridOptions();
  private config = columns(TABLE_NAME);

  render() {
    const { mainData, loading } = this.props.store;

    return (
      <div className={styles.wrapper}>
        <Dialog
          title={`Удаление проекта`}
          isOpen={this.state.showRemoveOrder}
          onClose={this.toggleShowRemoveOrder}
          backdropClassName="standard"
          className="figma-dialog"
        >
          <div className={Classes.DIALOG_BODY}>
            <div className={styles.dialogBody}>
              {`Вы точно хотите удалить договор ${this.state.request?.inventoryNumber} - ${this.state.request?.targetName}?`}
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton
                    size="small"
                    type="danger"
                    onClick={() => {
                      this.setRemoved(this.state.request);
                    }}
                  >
                    Удалить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.toggleShowRemoveOrder}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        <Dialog
          title={`Удаление заявки`}
          isOpen={this.state.showRemoveAgentRequest}
          onClose={this.toggleShowRemoveAgentRequest}
          backdropClassName="standard"
          className="figma-dialog"
        >
          <div className={Classes.DIALOG_BODY}>
            <div className={styles.dialogBody}>
              {`Вы точно хотите удалить заявку от агента ${this.state.request?.targetName}?`}
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton
                    size="small"
                    type="danger"
                    onClick={() => {
                      this.removeAgentRequest(this.state.request);
                    }}
                  >
                    Удалить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.toggleShowRemoveAgentRequest}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {loading ? (
          <div>
            <LocalLoader active={loading} />
          </div>
        ) : (
          <div>
            <div style={{ marginLeft: "-16px", width: "100%" }}>
              <Search
                query={this.props.store.query}
                onSearch={this.props.store.setQuery}
                style={{ background: "#fff" }}
              />
            </div>
            {mainData.length > 0 ? (
              <div className={styles.tableContainer}>
                <GridTheme bordered={true} className={`designed ${styles.table}`}>
                  <AgGridReact
                    rowStyle={{
                      fontFamily: "GothamPro, Arial, sans-serif",
                      color: "#00273D",
                    }}
                    rowHeight={44}
                    headerHeight={32}
                    rowSelection="single"
                    suppressCellFocus={true}
                    enableCellTextSelection={true}
                    columnDefs={this.config}
                    rowData={mainData}
                    onRowDoubleClicked={this.onRowSelected}
                    onColumnResized={sizeSaver}
                    gridOptions={this.options}
                    rowBuffer={MAX_INT}
                    context={this}
                  />
                </GridTheme>
              </div>
            ) : (
              <div className={styles.noData}>Нет запросов</div>
            )}
          </div>
        )}
      </div>
    );
  }

  setShowRemove = (request: MainPageRequestItemType) => {
    this.setState({ request });
    this.toggleShowRemoveOrder();
  };

  setRemoved = async (request: MainPageRequestItemType | null) => {
    if (request !== null) {
      await this.props.store.removeOrder(request.targetId, request.id);
      this.setState({ request: null });
      this.toggleShowRemoveOrder();
    }
  };

  setCancel = async (request: MainPageRequestItemType) => {
    await this.props.store.cancelOrderRemove(request.targetId, request.id);
  };

  setConfirmUpload = async (request: MainPageRequestItemType) => {
    await this.props.store.confirmOrderUpload(request.targetId, request.id);
  };

  setRejectUpload = async (request: MainPageRequestItemType) => {
    await this.props.store.rejectOrderUpload(request.targetId, request.id);
  };

  setApproveOutsourcer = async (request: MainPageRequestItemType) => {
    await this.props.store.approveOutsourcer(request.targetId, request.id);
  };

  setRejectOutsourcer = async (request: MainPageRequestItemType) => {
    await this.props.store.rejectOutsourcer(request.targetId, request.id);
  };

  setApproveAgentRequest = async (request: MainPageRequestItemType) => {
    await this.props.store.approveAgentRequest(request.targetId, request.id);
  };

  setShowRemoveAgentRequest = (request: MainPageRequestItemType) => {
    this.setState({ request });
    this.toggleShowRemoveAgentRequest();
  };

  removeAgentRequest = async (request: MainPageRequestItemType | null) => {
    if (request !== null) {
      await this.props.store.removeAgentRequest(request.targetId, request.id);
      this.setState({ request: null });
      this.toggleShowRemoveAgentRequest();
    }
  };

  // переход по двойному клику: одиночный клик оставлен кнопкам в столбце «Действия»
  onRowSelected = ({ data }: RowDoubleClickedEvent) => {
    if (data.type === RequestTypes.agent) {
      window.open(routes.agents.outsourcers.details.outsourcers(data.targetId), "_blank");
      return;
    }

    // у заявки своего маршрута нет: открываем список контрагентов, где заявка развернётся диалогом
    if (data.type === RequestTypes.agentRequest) {
      window.open(`${routes.agents.outsourcers.outsourcers}?request=${data.targetId}`, "_blank");
      return;
    }

    window.open(routes.orders.details(data.targetId, OrderTabs.summary), "_blank");
  };

  toggleShowRemoveOrder = () => {
    this.setState({ showRemoveOrder: !this.state.showRemoveOrder });
  };

  toggleShowRemoveAgentRequest = () => {
    this.setState({ showRemoveAgentRequest: !this.state.showRemoveAgentRequest });
  };
}

export const RequestList = observer(RequestItems);

interface RequestListProps {
  store: MainPageRequestsStoreType;
}

interface RequestListState {
  showRemoveOrder: boolean;
  showRemoveAgentRequest: boolean;
  request: MainPageRequestItemType | null;
}
